import { default as Input } from 'antd/es/input';
import { default as Modal } from 'antd/es/modal';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import type { IntegrationBusinessUnit } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import {
  RENAME_BUSINESS_UNIT_CANCEL_LABEL,
  RENAME_BUSINESS_UNIT_CANCEL_TEST_ID,
  RENAME_BUSINESS_UNIT_FORM_ID,
  RENAME_BUSINESS_UNIT_MODAL_TEST_ID,
  RENAME_BUSINESS_UNIT_NAME_LABEL,
  RENAME_BUSINESS_UNIT_NAME_PLACEHOLDER,
  RENAME_BUSINESS_UNIT_NAME_TEST_ID,
  RENAME_BUSINESS_UNIT_SUBMIT_LABEL,
  RENAME_BUSINESS_UNIT_SUBMIT_TEST_ID,
  RENAME_BUSINESS_UNIT_TITLE
} from './constants';
import { useRenameBusinessUnitForm } from './useRenameBusinessUnitForm';

type RenameBusinessUnitModalProps = {
  businessUnit: IntegrationBusinessUnit;
  open: boolean;
  onClose: () => void;
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <label className="mb-0.5 flex gap-0.5 text-xs font-medium text-gray-500">
    {children}
    <span className="text-feedback-error">*</span>
  </label>
);

export const RenameBusinessUnitModal = ({
  businessUnit,
  open,
  onClose
}: RenameBusinessUnitModalProps) => {
  const { form, submit, cancel, isSubmitting } = useRenameBusinessUnitForm({
    businessUnit,
    onClose
  });
  const {
    control,
    formState: { isValid }
  } = form;

  return (
    <Modal
      open={open}
      onCancel={cancel}
      width={520}
      destroyOnHidden
      title={
        <div className="flex items-center justify-between pr-6">
          <span className="text-xl font-medium">
            {RENAME_BUSINESS_UNIT_TITLE}
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton
            data-testid={RENAME_BUSINESS_UNIT_CANCEL_TEST_ID}
            onClick={cancel}
          >
            {RENAME_BUSINESS_UNIT_CANCEL_LABEL}
          </SecondaryButton>
          <PrimaryButton
            data-testid={RENAME_BUSINESS_UNIT_SUBMIT_TEST_ID}
            form={RENAME_BUSINESS_UNIT_FORM_ID}
            htmlType="submit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {RENAME_BUSINESS_UNIT_SUBMIT_LABEL}
          </PrimaryButton>
        </div>
      }
    >
      <form
        id={RENAME_BUSINESS_UNIT_FORM_ID}
        onSubmit={submit}
        data-testid={RENAME_BUSINESS_UNIT_MODAL_TEST_ID}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col">
          <FieldLabel>{RENAME_BUSINESS_UNIT_NAME_LABEL}</FieldLabel>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                data-testid={RENAME_BUSINESS_UNIT_NAME_TEST_ID}
                placeholder={RENAME_BUSINESS_UNIT_NAME_PLACEHOLDER}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                className="h-12 rounded-2xl"
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};
